import React from 'react';
import { Link } from 'react-router-dom';
import ClubSearch from '../components/shared/ClubSearch';

const LandingPage = () => {
    const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
    const [selectedClub, setSelectedClub] = React.useState(null);
    const [isClubModalOpen, setIsClubModalOpen] = React.useState(false);

    // Función para manejar la selección de un club
    const handleClubSelect = (club) => {
        setSelectedClub(club);
        setIsClubModalOpen(true);
    };

    const backgroundImages = [
        '/padel_arena.png',
        '/padel_celebration.png',
        '/padel_highlight.png',
        '/padel_action.png'
    ];

    React.useEffect(() => {
        document.title = 'VisioPadel - Organiza y Juega Pozos de Pádel';

        const interval = setInterval(() => {
            setCurrentImageIndex((prevIndex) => (prevIndex + 1) % backgroundImages.length);
        }, 6000); 

        return () => clearInterval(interval);
    }, []);

    return (
        <div className="min-h-screen bg-slate-50 font-sans">
            {/* Navbar */}
            <nav className="fixed top-0 left-0 w-full z-50 bg-[#0f172a]/95 backdrop-blur-md h-20 md:h-24 transition-all border-b border-white shadow-lg">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
                    <div className="flex justify-between items-center h-full">
                        <Link to="/" className="flex items-center group">
                            <img 
                                src="/assets/SVG/LogoVisioPadelBlanco.svg?v=2" 
                                alt="VisioPadel" 
                                className="h-14 md:h-20 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                            />
                        </Link>

                        <div className="hidden md:flex items-center gap-8 text-[15px] font-light text-slate-200">
                             <div className="flex flex-col items-center opacity-60 cursor-not-allowed">
                                <div className="flex items-center gap-1.5 whitespace-nowrap text-slate-400">
                                    Gestiona tu club
                                </div>
                                <span className="text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider border border-slate-700 -mt-1">Próximamente</span>
                            </div>
                            <Link to="/pricing" className="nav-link-xporty hover:text-white transition-colors">Organiza tu evento</Link>
                            <Link to="/about" className="nav-link-xporty hover:text-white transition-colors">Cómo Funciona</Link>
                            <Link to="/login" className="nav-link-xporty hover:text-white transition-colors">Entrar</Link>
                            <Link to="/signup" className="nav-link-xporty hover:text-white transition-colors">Registrarse</Link>
                        </div>



                        {/* Mobile Menu Button - Simplified */}
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="md:hidden p-2 text-white"
                        >
                             <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Mobile Menu Content */}
                {isMobileMenuOpen && (
                    <div className="md:hidden bg-[#0f172a] border-t border-white/10 py-6 animate-in slide-in-from-top duration-300">
                        <div className="flex flex-col gap-4 px-6">
                            <Link 
                                to="/pricing" 
                                className="text-lg font-medium text-slate-200 hover:text-white py-2 border-b border-white/5"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                Organiza tu evento
                            </Link>
                            <Link 
                                to="/about" 
                                className="text-lg font-medium text-slate-200 hover:text-white py-2 border-b border-white/5"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                Cómo Funciona
                            </Link>
                            <Link 
                                to="/login" 
                                className="text-lg font-medium text-slate-200 hover:text-white py-2 border-b border-white/5"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                Entrar
                            </Link>
                            <Link 
                                to="/signup" 
                                className="bg-[var(--primary-brand)] text-white text-center py-4 rounded-2xl font-bold mt-2 shadow-lg shadow-[var(--primary-brand)]/20"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                Registrarse
                            </Link>
                        </div>
                    </div>
                )}
            </nav>

            {/* Main Hero - Adapted aesthetic */}
            <main>
                <div className="relative pt-24 pb-16 md:pt-36 md:pb-24 overflow-hidden">
                    {/* Background with Overlay */}
                    <div className="absolute inset-0 z-0">
                         {backgroundImages.map((image, index) => (
                            <div
                                key={image}
                                className={`absolute inset-0 bg-cover bg-center transition-opacity duration-[2000ms] ${index === currentImageIndex ? 'opacity-100' : 'opacity-0'}`}
                                style={{ backgroundImage: `url('${image}')` }}
                            ></div>
                        ))}
                        <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-[2px]"></div>
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent"></div>
                    </div>

                    <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
                        <span className="inline-block py-1 px-3 rounded-full bg-white/10 text-white/90 backdrop-blur-md border border-white/20 text-sm font-semibold mb-4 tracking-wide uppercase">
                            La plataforma del pádel amateur
                        </span>
                        
                        <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold text-white mb-4 tracking-tight leading-none shadow-black/50 drop-shadow-lg">
                            Visio<span className="text-[var(--primary-brand)]">Padel</span>
                        </h1>
                        
                        <p className="text-xl md:text-2xl text-slate-100 mb-8 max-w-2xl mx-auto font-medium leading-relaxed drop-shadow-md">
                            La forma más sencilla de gestionar tus torneos, pozos y clubes. O simplemente, encuentra dónde jugar.
                        </p>

                        <div className="max-w-xl mx-auto bg-white/95 backdrop-blur-xl p-2 rounded-3xl shadow-2xl mb-8">
                            <ClubSearch 
                                onSelect={handleClubSelect} 
                                placeholder="Busca un club para jugar..." 
                            />
                        </div>

                        <div className="flex flex-wrap justify-center gap-4">
                            <Link to="/events" className="bg-white/10 backdrop-blur-md border border-white/30 text-white px-8 py-4 rounded-2xl font-bold hover:bg-white hover:text-slate-900 transition-all">
                                Ver Eventos Estilo VisioPadel
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Features Split - Card Style */}
                <div className="max-w-7xl mx-auto px-4 -mt-20 relative z-20 pb-20">
                    <div className="grid md:grid-cols-2 gap-8">
                        {/* Player Card */}
                        <div className="bg-[#0f172a] rounded-3xl p-8 md:p-12 shadow-xl hover:shadow-2xl hover:shadow-[var(--primary-brand)]/10 transition-all duration-300 border-2 border-white group transform hover:-translate-y-2 hover:scale-[1.02]">
                            <div className="w-16 h-16 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center mb-6 text-3xl group-hover:scale-110 transition-transform shadow-inner">
                                🎾
                            </div>
                            <h2 className="text-3xl font-extrabold text-white mb-4">Para Jugadores</h2>
                            <p className="text-slate-300 text-lg mb-8 leading-relaxed font-light">
                                Encuentra torneos, apúntate a pozos con un clic y lleva un registro de tus partidos. Todo en un solo lugar.
                            </p>
                            <Link to="/events" className="inline-flex items-center justify-center w-full px-6 py-4 bg-slate-500/10 backdrop-blur-sm border border-slate-400/30 text-white text-lg font-bold rounded-full hover:bg-slate-500/20 hover:border-slate-400/50 transition-all duration-300 gap-2 group-hover:gap-3">
                                Buscar Eventos <span className="text-xl transition-transform group-hover:translate-x-1">&rarr;</span>
                            </Link>
                        </div>

                        {/* Organizer Card */}
                        <div className="bg-[#0f172a] rounded-3xl p-8 md:p-12 shadow-xl hover:shadow-2xl hover:shadow-[var(--primary-brand)]/10 transition-all duration-300 border-2 border-white group transform hover:-translate-y-2 hover:scale-[1.02]">
                            <div className="w-16 h-16 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center mb-6 text-3xl group-hover:scale-110 transition-transform shadow-inner">
                                🏆
                            </div>
                            <h2 className="text-3xl font-extrabold text-white mb-4">Para Organizadores</h2>
                            <p className="text-slate-300 text-lg mb-8 leading-relaxed font-light">
                                Automatiza inscripciones, cobros y cuadros. Deja de usar Excel y WhatsApp. Profesionaliza tu gestión.
                            </p>
                            <Link to="/signup" className="inline-flex items-center justify-center w-full px-6 py-4 bg-slate-500/10 backdrop-blur-sm border border-slate-400/30 text-white text-lg font-bold rounded-full hover:bg-slate-500/20 hover:border-slate-400/50 transition-all duration-300 gap-2 group-hover:gap-3">
                                Crear mi espacio gratis <span className="text-xl transition-transform group-hover:translate-x-1">&rarr;</span>
                            </Link>
                        </div>
                    </div>
                </div>
            </main>

            {/* Footer */}
             <footer className="bg-white border-t border-slate-200 py-12">
                <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-8">
                    <img src="/visiopadel-logo.png" alt="VisioPadel" className="h-10 opacity-70 grayscale hover:grayscale-0 transition-all" />
                    <p className="text-slate-400 text-sm font-bold uppercase tracking-widest">© 2026 VisioPadel</p>
                </div>
            </footer>

            {/* Modal de Detalles del Club copy-pasted/adapted logic */}
            {isClubModalOpen && selectedClub && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-300">
                    <div 
                        className="bg-white rounded-[2.5rem] shadow-2xl max-w-lg w-full overflow-hidden animate-in zoom-in-95 duration-300 relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                         <div className="h-32 bg-gradient-to-r from-[#72c02c] to-[#65a30d] relative">
                            <button 
                                onClick={() => setIsClubModalOpen(false)}
                                className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/40 rounded-full text-white transition-all"
                            >
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                             <div className="absolute -bottom-10 left-8">
                                <div className="w-20 h-20 bg-white rounded-2xl shadow-xl flex items-center justify-center text-3xl">
                                    🏟️
                                </div>
                            </div>
                        </div>

                        <div className="pt-14 pb-10 px-8">
                            <h3 className="text-2xl font-black text-slate-900 mb-2 leading-tight">
                                {selectedClub.n}
                            </h3>
                             <p className="text-slate-500 mb-6">{selectedClub.d}</p>
                            
                            <div className="bg-slate-50 rounded-3xl p-6 border border-slate-100">
                                <h4 className="font-bold text-slate-900 mb-2">VisioPadel en tu Club</h4>
                                <p className="text-sm text-slate-500 mb-4">Contacta con nosotros para activar la gestión digital en {selectedClub.n}.</p>
                                <Link to="/signup" className="block text-center bg-slate-900 text-white py-3 rounded-xl font-bold hover:bg-slate-800 transition-colors">
                                    Soy Gestor de este Club
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LandingPage;
